import React, { ReactNode } from 'react';

import { Box } from '../Box';
import { Typography } from '../Typography';
import { Link } from '../Link';

export interface DialogContentProps {
  onClose: () => void;
  title?: string;
  titleChip?: ReactNode;
  fullScreen?: boolean;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  withoutPadding?: boolean;
}

/**
 * Компонент DialogContent представляет содержимое диалогового окна.
 * @param {Function} onClose - Функция, вызываемая при закрытии диалогового окна.
 * @param {string} [title] - Заголовок диалогового окна.
 * @param {ReactNode} [titleChip] - Дополнительный компонент, отображаемый рядом с заголовком.
 * @param {boolean} [fullScreen] - Флаг, определяющий, занимает ли диалоговое окно весь экран.
 * @param {ReactNode} [actions] - Компоненты действий, отображаемые в нижней части диалогового окна.
 * @param {ReactNode} [children] - Дочерние элементы, отображаемые в основном содержимом диалогового окна.
 * @param {ReactNode} [withoutPadding] - Убирает отступы внутри диалога, не затрагивая заголовок.
 */

export const DialogContent = ({
  children,
  fullScreen,
  title,
  titleChip,
  actions,
  onClose,
  withoutPadding = false,
}: DialogContentProps) => {
  const pySpace = fullScreen ? '32' : '48';
  const pxSpace = fullScreen ? '16' : '48';

  return (
    <Box display="flex" flexDirection="column" flexGrow="1">
      <Box py={pySpace} px={withoutPadding ? 'unset' : pxSpace} flexGrow="1">
        {title && (
          <Box
            display="flex"
            alignItems="flex-start"
            mb="24"
            flexDirection="column"
            px={withoutPadding ? pxSpace : 'unset'}
          >
            {fullScreen && (
              <Box mb="8">
                <Link size="small" onClick={onClose} stroke={false}>
                  Назад
                </Link>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography variant="h5">{title}</Typography>
              {titleChip}
            </Box>
          </Box>
        )}
        {children}
      </Box>
      {actions &&
        (fullScreen ? (
          <Box
            display="flex"
            justifyContent="center"
            gap="16"
            px={withoutPadding ? 'unset' : '16'}
            pb="24"
          >
            {actions}
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="flex-end"
            gap="16"
            p="16"
            bgcolor="bg.greyscale.primary"
          >
            {actions}
          </Box>
        ))}
    </Box>
  );
};
